import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(
  input: string | number,
  locale: Intl.LocalesArgument = 'en-US'
): string {
  const date = new Date(input)
  return date.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

// Canonical production origin. Used as a fallback so absolute URLs (sitemap,
// OG image, canonical tags) never resolve to `undefined/...` when
// NEXT_PUBLIC_APP_URL is not set in the build/runtime environment.
// Must stay in sync with siteConfig.url — it is duplicated rather than imported
// because config/site.ts imports absoluteUrl from this module, and importing
// back would close a cycle. Apex host, not www: www 301s here.
export const SITE_URL = 'https://euclideum.com'

/**
 * Absolute URL for canonicals, the sitemap and OG tags.
 *
 * NEXT_PUBLIC_APP_URL is honoured so preview deployments describe themselves,
 * but the host is normalised first: a `www.` prefix left in a dashboard
 * environment variable would silently reintroduce the duplicate-hostname split
 * this site was just consolidated out of — every canonical would point at a
 * host that 301s away. Normalising here means the correct host cannot be
 * undone by configuration outside the repo. A trailing slash is dropped too,
 * since every caller passes a path beginning with "/".
 */
export function absoluteUrl(path: string) {
  const base = (process.env.NEXT_PUBLIC_APP_URL || SITE_URL)
    .replace(/\/+$/, '')
    .replace(/^(https?:\/\/)www\./i, '$1')

  return `${base}${path}`
}

export function truncateText(text: string, maxLength: number = 105) {
  if (text?.length > maxLength) {
    return text.slice(0, maxLength) + '...'
  }

  return text
}

export const isDev = process.env.NODE_ENV === 'development'
